<template>
  <template v-if="!!$slots.default">
    <a class="e-site-link" v-bind="componentAttrs" :aria-label="ariaLabel" v-if="isExternal">
      <slot />
    </a>
    <router-link
      class="e-site-link"
      v-bind="$attrs"
      :to="href"
      :aria-label="ariaLabel"
      v-else-if="!$isNoE(href)"
    >
      <slot />
    </router-link>
    <span
      class="e-site-link"
      v-bind="$attrs"
      :aria-label="ariaLabel"
      :tabindex="0"
      @keydown="handleKeyDown"
      @click="handleSpanClick"
      v-else
    >
      <slot />
    </span>
  </template>
  <template v-else-if="href">
    <jss-link
      class="e-site-link"
      v-bind="$attrs"
      :field="link"
      :aria-label="ariaLabel"
      v-if="isExternal"
      @click="onJssLinkClick"
    />
    <router-link class="e-site-link" v-bind="$attrs" :to="href" :aria-label="ariaLabel" v-else>{{
      link.value?.text
    }}</router-link>
  </template>
</template>

<script>
import { computed, onMounted, reactive, toRefs, watch } from 'vue';
import { openWindow } from '@/utils/dom-utils';
import { appendQuery, getBetterUrl, isAbsoluteUri } from '@/utils/uri-utils';
import { useRouter } from 'vue-router';
import { isNoE } from '@/utils/obj-utils';
import * as querystring from 'querystring';

export default {
  name: 'SiteLink',
  inheritAttrs: false,
  props: {
    link: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const router = useRouter();
    const getHref = () => {
      let url = props.link?.value.href;
      if (/^(mailto|tel):/gi.test(url)) {
        return url;
      }
      if (/^https?:\/\/((tel|mailto):|#)/gi.test(url)) {
        return url.replace(/^https?:\/\//gi, '');
      }

      // 处理link对象中的Query string字段
      const queryString = props.link?.value?.querystring;
      if (!isNoE(queryString)) {
        try {
          const queryParams = querystring.parse(queryString);
          url = appendQuery(url, queryParams);
        } catch (error) {
          console.warn('Failed to parse querystring:', queryString, error);
        }
      }

      const anchor = props.link?.value?.anchor;
      if (!isNoE(anchor)) {
        url = url + '?id=' + anchor;
      }

      const { lang = 'en' } = router.currentRoute.value?.params ?? {};
      const { aplus_track_debug_id } = router.currentRoute.value?.query ?? {};
      if (!isNoE(aplus_track_debug_id)) {
        url = appendQuery(url, {
          aplus_track_debug_id,
        });
      }
      return getBetterUrl(url, lang) ?? '';
    };
    const state = reactive({
      href: getHref(),
      isExternal: false,
    });
    const computes = {
      componentAttrs: computed(() => {
        let result = {
          ...(props.link?.value ?? {}),
          ...(ctx.attrs ?? {}),
          href: state.href,
        };
        if (ctx.slots.default && result.text) {
          delete result.text;
        }
        return result;
      }),
      ariaLabel: computed(() => {
        // Prioritize passed aria-label prop over link text
        return ctx.attrs['aria-label'] || props.link?.value?.text || '';
      }),
    };
    const methods = {
      onJssLinkClick(e) {
        e.preventDefault();
        openWindow(getHref(), props.link?.value.target);
        return false;
      },
      handleKeyDown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          // Trigger click event for span elements
          e.target.click();
        }
      },
      handleSpanClick(e) {
        // If there's a valid href, open it
        if (state.href && state.href.trim()) {
          if (state.isExternal) {
            openWindow(state.href, props.link?.value.target);
          } else {
            // For internal links, navigate using router
            router.push(state.href);
          }
        }
      },
    };
    watch(
      () => props.link,
      (link) => {
        state.href = getHref();
        const target = link?.value?.target;
        const hasExplicitTarget = !isNoE(target) && target !== '_self';
        state.isExternal =
          isAbsoluteUri(state.href) ||
          ['mailto', 'tel', 'external'].includes(link?.value.linktype) ||
          hasExplicitTarget;
      },
      {
        deep: true,
        immediate: true,
      }
    );
    onMounted(() => {
      state.href = getHref();
    });
    return {
      ...toRefs(state),
      ...computes,
      ...methods,
      ariaLabel: computes.ariaLabel,
    };
  },
};
</script>

<style lang="scss">
.e-site-link {
  outline: none;

  &:focus {
    outline: none;
  }

  &[role='button'] {
    cursor: pointer;
    outline: none;
  }
}
</style>
